import { FormControl, FormField, FormItem, FormLabel } from '@/common/ui/form'
import { Control } from 'react-hook-form'
import useQueryGetBankProvider from '@/modules/bank/api/hooks/useQueryGetBankProvider'
import { Skeleton } from '@/common/ui/skeleton'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import { cn } from '@/common/lib/utils'
import { useState, useMemo } from 'react'

interface BaseFieldProps {
	control: Control<any>
	label?: string
	name?: string
	required?: boolean
}

export function BankSelector({
	control,
	label = 'ธนาคาร',
	name = 'bankProvider',
	required = true,
}: BaseFieldProps) {
	const { data, isLoading } = useQueryGetBankProvider()
	const [open, setOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')

	const filteredBanks = useMemo(() => {
		if (!data) return []
		if (!searchQuery) return data
		return data.filter(
			(bank) =>
				bank.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
				bank.nameTH.toLowerCase().includes(searchQuery.toLowerCase()),
		)
	}, [data, searchQuery])

	if (isLoading || !data) {
		return <Skeleton className="h-10 w-full" />
	}

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => {
				const selectedBank = data.find((bank) => bank.name === field.value)

				return (
					<FormItem>
						<FormLabel>
							{required && <RequiredIndicator />} {label}
						</FormLabel>
						<FormControl>
							<Popover modal={true} open={open} onOpenChange={setOpen}>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={open}
										className={cn(
											'w-full justify-between',
											!field.value && 'text-muted-foreground',
										)}
									>
										<span className="truncate">
											{open && searchQuery
												? searchQuery
												: selectedBank
													? `${selectedBank.name} - ${selectedBank.nameTH}`
													: label}
										</span>
										<CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent
									className="z-[100] p-0"
									align="start"
									style={{ width: 'var(--radix-popover-trigger-width)' }}
								>
									<Input
										placeholder="ค้นหาธนาคาร..."
										className="border-0 focus-visible:ring-0"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
									<div className="max-h-[200px] overflow-y-auto overscroll-contain">
										{filteredBanks.length > 0 ? (
											filteredBanks.map((bank) => (
												<div
													key={bank.id}
													className={cn(
														'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
														field.value === bank.name &&
															'bg-accent text-accent-foreground',
													)}
													onClick={() => {
														field.onChange(bank.name)
														setOpen(false)
														setSearchQuery('')
													}}
												>
													<div className="flex items-center gap-2">
														<span className="text-xs text-muted-foreground">
															{bank.name}
														</span>
														<span>{bank.nameTH}</span>
													</div>
													{field.value === bank.name && (
														<CheckIcon className="ml-auto h-4 w-4" />
													)}
												</div>
											))
										) : (
											<div className="py-6 text-center text-sm text-muted-foreground">
												ไม่พบธนาคาร
											</div>
										)}
									</div>
								</PopoverContent>
							</Popover>
						</FormControl>
					</FormItem>
				)
			}}
		/>
	)
}
